/**
 * Coalesced refetches for stream events. Events come in bursts: one cron run that marks 40 tasks overdue sends 40
 * task.overdue events, and the deal follow-ups a minute later send 38 more. Each event used to refetch every matching
 * query at once, in every open tab, so one office with the Home screen open sent thousands of requests a minute and
 * met the API's per-address limit (429), which then failed the screens it was refreshing.
 *
 * Keys collected here are invalidated together: the first flush LEAD_MS after a quiet spell (a lone event still feels
 * live), the next no sooner than GAP_MS after the one before, so a query refetches at most once per GAP_MS however many
 * events arrive. A key already covered by a shorter one in the same flush (['crm'] covers ['crm', 'tasks'], [] covers
 * everything) is dropped. Pure: no React, no TanStack (core/refetch.ts wires it to the query client).
 */
export type Key = readonly unknown[];

export const LEAD_MS = 300;
export const GAP_MS = 3_000;

export interface Timers {
  now(): number;
  set(fn: () => void, ms: number): unknown;
  clear(id: unknown): void;
}
const realTimers: Timers = {
  now: () => Date.now(),
  set: (fn, ms) => setTimeout(fn, ms),
  clear: (id) => clearTimeout(id as ReturnType<typeof setTimeout>),
};

const part = (v: unknown) => JSON.stringify(v) ?? String(v);

/** Does invalidating `prefix` already invalidate `key`? (Element by element; never drops a key it is unsure of.) */
export function covers(prefix: Key, key: Key): boolean {
  return prefix.length <= key.length && prefix.every((p, i) => part(p) === part(key[i]));
}

/** The keys without duplicates and without those a shorter key in the list already covers. */
export function minimalKeys(keys: readonly Key[]): Key[] {
  const out: Key[] = [];
  for (const k of [...keys].sort((a, b) => a.length - b.length)) if (!out.some((o) => covers(o, k))) out.push(k);
  return out;
}

export interface Coalescer {
  add(key: Key): void;
  /** Run what is pending now (tests, teardown). */
  flush(): void;
}

export function createCoalescer(
  run: (keys: Key[]) => void,
  { timers = realTimers, leadMs = LEAD_MS, gapMs = GAP_MS }: { timers?: Timers; leadMs?: number; gapMs?: number } = {},
): Coalescer {
  const pending = new Map<string, Key>();
  let timer: unknown = null;
  let last = -Infinity;
  const fire = () => {
    timer = null;
    if (!pending.size) return;
    const keys = minimalKeys([...pending.values()]);
    pending.clear();
    last = timers.now();
    run(keys);
  };
  return {
    add(key) {
      pending.set(part(key), key);
      if (timer != null) return;
      timer = timers.set(fire, Math.max(leadMs, last + gapMs - timers.now()));
    },
    flush() {
      if (timer != null) timers.clear(timer);
      fire();
    },
  };
}
